import { Errors } from '@/constants/errors';
import { stripeService } from '@/services/stripe';
import { PaymentMethodEnum } from '@/types/common';
import type { ICheckoutContext } from '@/types/context';
import { isStripePaymentResponse } from '@/types/guards';
import { FinalizeStatus, StripePaymentIntentStatus, type StripePaymentResponse } from '@/types/payment';
import type { BlikPaymentParams } from '@/types/paymentHandlers';
import { buildStripeBlikPaymentRequest } from '@/utils/stripe';

export const handleBlikPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: BlikPaymentParams
): Promise<unknown> => {
  const code = params?.code?.trim();
  const paymentMethodSettings = checkout.paymentMethodsSettings.find(
    (pm) => pm.paymentMethodName === PaymentMethodEnum.BLIK
  );

  if (!code || !paymentMethodSettings) {
    throw new Error(Errors.payment.paymentFailed);
  }

  const finalizeResponse = await sendPaymentToServer<StripePaymentResponse>(
    buildStripeBlikPaymentRequest(paymentMethodSettings, checkout.customer)
  );

  if (!isStripePaymentResponse(finalizeResponse) || !finalizeResponse.stripe?.clientSecret) {
    return finalizeResponse;
  }

  const { paymentIntent, error } = await stripeService.confirmBlikPayment(
    finalizeResponse.stripe.clientSecret,
    code,
    checkout.customer.email
  );

  if (error) {
    return {
      ...finalizeResponse,
      stripe: { ...finalizeResponse.stripe, status: FinalizeStatus.FAILED },
      appChargeErrorType: stripeService.mapBlikErrorCode(error.code ?? error.payment_intent?.last_payment_error?.code),
    } satisfies StripePaymentResponse;
  }

  if (paymentIntent?.status === StripePaymentIntentStatus.SUCCEEDED) {
    return {
      ...finalizeResponse,
      stripe: { ...finalizeResponse.stripe, status: FinalizeStatus.SUCCESS, intentId: paymentIntent.id },
    } satisfies StripePaymentResponse;
  }

  return {
    ...finalizeResponse,
    stripe: {
      ...finalizeResponse.stripe,
      status: FinalizeStatus.FAILED,
      intentId: paymentIntent?.id ?? finalizeResponse.stripe.intentId,
    },
    appChargeErrorType: stripeService.mapBlikErrorCode(paymentIntent?.last_payment_error?.code),
  } satisfies StripePaymentResponse;
};
